import { supabase } from '@/lib/supabase';
import type {
  ActivityAlert,
  ActivityAlertRule,
  ActivityAlertStatus,
  ActivityCursor,
  ActivityKpis,
  ActivityLogFilters,
  ActivityLogRow,
  ActivityPage,
  AdminActionLogRow,
  LoginLogFilters,
  LoginLogRow,
} from '@/types/activityLog';

export const ACTIVITY_RPC = {
  activityLog: 'admin_get_activity_log',
  loginLog: 'admin_get_login_log',
  alerts: 'admin_get_activity_alerts',
  alertRules: 'admin_get_activity_alert_rules',
  updateAlertStatus: 'admin_update_alert_status',
  updateAlertRule: 'admin_update_alert_rule',
  adminActions: 'admin_get_merged_audit_log',
  kpis: 'admin_get_activity_kpis',
} as const;

export const ACTIVITY_RPC_ARGS = {
  afterTs: 'after_ts',
  afterId: 'after_id',
  orgId: 'p_org_id',
  userId: 'p_user_id',
  action: 'p_action',
  actionPattern: 'p_action_pattern',
  entityType: 'p_entity_type',
  severity: 'p_severity',
  event: 'p_event',
  fromTs: 'p_date_from',
  toTs: 'p_date_to',
  search: 'p_search',
  limit: 'p_limit',
  alertId: 'p_alert_id',
  status: 'p_status',
  ruleKey: 'p_rule_key',
  threshold: 'p_threshold',
  enabled: 'p_enabled',
} as const;

const PAGE_SIZE = 50;
const ALERT_PAGE_SIZE = 100;
const MAX_ALERT_PAGES = 10_000;

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function pageFromRows<T extends { id: string; occurred_at: string }>(
  rows: T[],
): ActivityPage<T> {
  const hasMore = rows.length > PAGE_SIZE;
  const items = hasMore ? rows.slice(0, PAGE_SIZE) : rows;
  const last = items.length ? items[items.length - 1] : undefined;
  return {
    items,
    nextCursor: hasMore && last ? { afterTs: last.occurred_at, afterId: last.id } : null,
  };
}

export const activityLogApi = {
  getActivityLog: async (
    filters: ActivityLogFilters,
    cursor: ActivityCursor | null,
  ): Promise<ActivityPage<ActivityLogRow>> => {
    const { data, error } = await supabase.rpc(ACTIVITY_RPC.activityLog, {
      [ACTIVITY_RPC_ARGS.afterTs]: cursor?.afterTs ?? null,
      [ACTIVITY_RPC_ARGS.afterId]: cursor?.afterId ?? null,
      [ACTIVITY_RPC_ARGS.orgId]: filters.orgId ?? null,
      [ACTIVITY_RPC_ARGS.userId]: filters.userId ?? null,
      [ACTIVITY_RPC_ARGS.action]: filters.action ?? null,
      [ACTIVITY_RPC_ARGS.actionPattern]: filters.actionPattern ?? null,
      [ACTIVITY_RPC_ARGS.entityType]: filters.entityType ?? null,
      [ACTIVITY_RPC_ARGS.severity]: filters.severity ?? null,
      [ACTIVITY_RPC_ARGS.fromTs]: filters.fromTs ?? null,
      [ACTIVITY_RPC_ARGS.toTs]: filters.toTs ?? null,
      [ACTIVITY_RPC_ARGS.search]: filters.search ?? null,
      [ACTIVITY_RPC_ARGS.limit]: PAGE_SIZE + 1,
    });
    if (error) throw error;

    const rows = ((data ?? []) as ActivityLogRow[]).map((row) => ({
      ...row,
      metadata: asObject(row.metadata),
    }));
    return pageFromRows(rows);
  },

  getLoginLog: async (
    filters: LoginLogFilters,
    cursor: ActivityCursor | null,
  ): Promise<ActivityPage<LoginLogRow>> => {
    const { data, error } = await supabase.rpc(ACTIVITY_RPC.loginLog, {
      [ACTIVITY_RPC_ARGS.afterTs]: cursor?.afterTs ?? null,
      [ACTIVITY_RPC_ARGS.afterId]: cursor?.afterId ?? null,
      [ACTIVITY_RPC_ARGS.orgId]: filters.orgId ?? null,
      [ACTIVITY_RPC_ARGS.userId]: filters.userId ?? null,
      [ACTIVITY_RPC_ARGS.event]: filters.event ?? null,
      [ACTIVITY_RPC_ARGS.fromTs]: filters.fromTs ?? null,
      [ACTIVITY_RPC_ARGS.toTs]: filters.toTs ?? null,
      [ACTIVITY_RPC_ARGS.search]: filters.search ?? null,
      [ACTIVITY_RPC_ARGS.limit]: PAGE_SIZE + 1,
    });
    if (error) throw error;

    const rows = ((data ?? []) as LoginLogRow[]).map((row) => ({
      ...row,
      metadata: asObject(row.metadata),
    }));
    return pageFromRows(rows);
  },

  getAlerts: async (): Promise<ActivityAlert[]> => {
    const allRows: ActivityAlert[] = [];
    let cursor: ActivityCursor | null = null;
    const seenCursors = new Set<string>();

    for (let page = 0; page < MAX_ALERT_PAGES; page += 1) {
      const { data, error } = await supabase.rpc(ACTIVITY_RPC.alerts, {
        [ACTIVITY_RPC_ARGS.limit]: ALERT_PAGE_SIZE,
        [ACTIVITY_RPC_ARGS.afterTs]: cursor?.afterTs ?? null,
        [ACTIVITY_RPC_ARGS.afterId]: cursor?.afterId ?? null,
        [ACTIVITY_RPC_ARGS.status]: null,
        [ACTIVITY_RPC_ARGS.ruleKey]: null,
        [ACTIVITY_RPC_ARGS.orgId]: null,
        [ACTIVITY_RPC_ARGS.userId]: null,
      });
      if (error) throw error;

      const rows = (data ?? []) as ActivityAlert[];
      allRows.push(...rows);
      if (rows.length < ALERT_PAGE_SIZE) break;

      const last = rows[rows.length - 1];
      const nextCursorKey = `${last.created_at}:${last.id}`;
      if (seenCursors.has(nextCursorKey)) {
        throw new Error('Activity alert pagination returned a repeated cursor.');
      }
      seenCursors.add(nextCursorKey);
      cursor = { afterTs: last.created_at, afterId: last.id };

      if (page === MAX_ALERT_PAGES - 1) {
        throw new Error('Activity alert pagination exceeded its safety limit.');
      }
    }

    return allRows
      .map((row) => ({
        ...row,
        evidence: asObject(row.evidence),
        rule_threshold: asObject(row.rule_threshold),
        rule_enabled: row.rule_enabled,
      }))
      .sort((a, b) => {
        if (a.status === 'new' && b.status !== 'new') return -1;
        if (a.status !== 'new' && b.status === 'new') return 1;
        return Date.parse(b.created_at) - Date.parse(a.created_at);
      });
  },

  getAlertRules: async (): Promise<ActivityAlertRule[]> => {
    const { data, error } = await supabase.rpc(ACTIVITY_RPC.alertRules);
    if (error) throw error;
    return ((data ?? []) as ActivityAlertRule[]).map((row) => ({
      ...row,
      threshold: asObject(row.threshold),
    }));
  },

  updateAlertStatus: async (
    alertId: string,
    status: Exclude<ActivityAlertStatus, 'new'>,
  ): Promise<void> => {
    const { error } = await supabase.rpc(ACTIVITY_RPC.updateAlertStatus, {
      [ACTIVITY_RPC_ARGS.alertId]: alertId,
      [ACTIVITY_RPC_ARGS.status]: status,
    });
    if (error) throw error;
  },

  updateAlertRule: async (
    ruleKey: string,
    threshold: Record<string, unknown>,
    enabled: boolean,
  ): Promise<void> => {
    const { error } = await supabase.rpc(ACTIVITY_RPC.updateAlertRule, {
      [ACTIVITY_RPC_ARGS.ruleKey]: ruleKey,
      [ACTIVITY_RPC_ARGS.threshold]: threshold,
      [ACTIVITY_RPC_ARGS.enabled]: enabled,
    });
    if (error) throw error;
  },

  getAdminActions: async (): Promise<AdminActionLogRow[]> => {
    const { data, error } = await supabase.rpc(ACTIVITY_RPC.adminActions, {
      [ACTIVITY_RPC_ARGS.limit]: 200,
    });
    if (error) throw error;
    return ((data ?? []) as AdminActionLogRow[]).map((row) => ({
      ...row,
      metadata: asObject(row.metadata),
    }));
  },

  getKpis: async (): Promise<ActivityKpis> => {
    const { data, error } = await supabase.rpc(ACTIVITY_RPC.kpis);
    if (error) throw error;
    const row = (Array.isArray(data) ? data[0] : data) as Partial<ActivityKpis> | null;
    return {
      actions_24h: Number(row?.actions_24h ?? 0),
      deletes_24h: Number(row?.deletes_24h ?? 0),
      access_denied_24h: Number(row?.access_denied_24h ?? 0),
      open_alerts: Number(row?.open_alerts ?? 0),
    };
  },
};
